import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { environment } from 'apps/web-app/src/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { forkJoin, of } from 'rxjs';
import { catchError, distinctUntilKeyChanged, filter, first, map, retry, skipWhile, switchMap, take } from 'rxjs/operators';
import { getSensorIcon } from '../../evja/utils/sensors';
import { buildChartSeries, getDefaultThreshold } from '../../evja/utils/store';
import { GraphqlService } from '../../services/graphql.service';
import { ProspectusService } from '../../services/prospectus.service';
import * as SensorNodesActions from '../actions/sensorNodes.actions';
import * as SensorsActions from '../actions/sensors.actions';
import * as SensorNodesSelectors from '../selectors/sensorNodes.selectors';
import { SensorNode } from '../../models';
import { DEFAULT_TIMEZONE } from '../../constants/timezone';
import tzLookup from 'tz-lookup';

@Injectable()
export class SensorNodesEffects {

  constructor(
    private actions$: Actions,
    private store: Store,
    private graphqlService: GraphqlService,
    private prospectusService: ProspectusService,
    private logger: NGXLogger
  ) { }

  loadSensorNodes$ = createEffect(() => this.actions$.pipe(
    ofType(SensorNodesActions.LoadSensorNodes),
    switchMap((s) =>
      s.sensorNodes.length > 0
        ? this.graphqlService.getSensorNodesLastPosition(
          {
            input: {
              sensorNodesSerials: s.sensorNodes.map(sensorNode => sensorNode.serialNumber),
              gpspFamily: environment.gpspSensor,
              timezoneFamily: environment.timezoneSensor
            },
          },
          {
            fetchPolicy: 'no-cache'
          }
        ).pipe(
          first(),
          map(positions => {
            return {
              positions: positions.data.getSensorNodesLastPosition,
              timezones: positions.data.getSensorNodesLastPosition.reduce((a, b) => {
                let { sensorNodeSerial, gpsp, timezone } = b;
                return { ...a, [sensorNodeSerial]: timezone ? timezone : DEFAULT_TIMEZONE };
              }, {}),
              allSerials: s.sensorNodes.map(sn => sn.serialNumber),
            };
          }),
          retry(3),
          catchError(() => of({ positions: [], timezones: {}, allSerials: s.sensorNodes.map(sn => sn.serialNumber) }))
        )
        : of({ positions: [], timezones: {}, allSerials: [] })
    ),
    // Fallback: per serial senza posizione GPS, cerca nel prospetto più recente
    switchMap((result: any) => {
      const serialsWithGps = new Set(
        result.positions
          .filter((p: any) => p.gpsp)
          .map((p: any) => p.sensorNodeSerial)
      );
      const serialsWithoutGps: string[] = (result.allSerials as string[])
        .filter(s => !serialsWithGps.has(s));

      if (serialsWithoutGps.length === 0) {
        return of(result);
      }

      // Query leggera per ogni serial senza posizione
      const gpsQueries$ = serialsWithoutGps.map(serial =>
        this.prospectusService
          .getProspectusesGps({ serial, active: true })
          .query.valueChanges.pipe(
            take(1),
            map((res: any) => {
              const latest = res?.data?.prospectuses?.[0];
              if (!latest?.gps) return null;
              let timezone: string = null;
              try {
                const [lat, lng] = latest.gps.split(',').map((c: string) => parseFloat(c.trim()));
                timezone = tzLookup(lat, lng);
              } catch (_) { /* coordinate non valide, timezone resta null */ }
              return { sensorNodeSerial: serial, gpsp: latest.gps, timezone };
            }),
            catchError(() => of(null))
          )
      );

      return forkJoin(gpsQueries$).pipe(
        map(fallbacks => {
          const validFallbacks = fallbacks.filter(Boolean);
          const fallbackTimezones = validFallbacks.reduce((acc, fb) => {
            if (fb.timezone) acc[fb.sensorNodeSerial] = fb.timezone;
            return acc;
          }, {});
          // Il fallback sostituisce la riga senza GPS dello stesso serial: l'adapter è ordinato e
          // in updateMany scarta il secondo update di un id già aggiornato.
          const fallbackSerials = new Set(validFallbacks.map(fb => fb.sensorNodeSerial));
          return {
            ...result,
            positions: [
              ...result.positions.filter((p: any) => !fallbackSerials.has(p.sensorNodeSerial)),
              ...validFallbacks,
            ],
            timezones: { ...result.timezones, ...fallbackTimezones },
          };
        })
      );
    }),
    switchMap((result: any) => [
      SensorNodesActions.SetSensorNodesPosition({ payload: result.positions }),
      SensorNodesActions.SetSensorNodesTimezones({ timezones: result.timezones })
    ])
  ));


  // EPWA-980: al LoadSensorNodes la centralina preferita viene selezionata nel
  // reducer (selectedSensorNodeSerialNumber), ma l'effect changeSelectedSensorNodeSerialNumber$
  // ascolta solo l'AZIONE ChangeSelected, non lo stato. Dopo EPWA-990 (setValue con
  // emitEvent:false sul dropdown) il valueChanges non scatta più al primo paint, quindi
  // la catena misure (SetSensors → LoadSensorsData → getDashboardMeasures → SetSensorsData)
  // non parte e le card della preferita restano a "--"/NO DATA finché non si cambia
  // centralina a mano. Qui ridispatchiamo esplicitamente ChangeSelected per la preferita
  // (stesso serial scelto dal reducer) così la catena misure parte al login.
  selectFavoriteOnLoad$ = createEffect(() => this.actions$.pipe(
    ofType(SensorNodesActions.LoadSensorNodes),
    filter(({ sensorNodes }) => sensorNodes.length > 0),
    map(({ sensorNodes, index }) => sensorNodes[index > 0 ? index : 0]?.serialNumber),
    filter((serialNumber): serialNumber is string => !!serialNumber),
    map((serialNumber) => SensorNodesActions.ChangeSelectedSensorNodeSerialNumber({ sensorNode: serialNumber }))
  ));

  changeSelectedSensorNodeSerialNumber$ = createEffect(() => this.actions$.pipe(
    ofType(SensorNodesActions.ChangeSelectedSensorNodeSerialNumber),
    skipWhile(payload => payload.sensorNode == ""),
    distinctUntilKeyChanged('sensorNode'),
    map((action) => action.sensorNode),
    switchMap((payload: string) =>
      this.store
        .select(
          SensorNodesSelectors.selectSensorNodeEntity, { serialNumber: payload }
        )
        .pipe(
          first()
        )),
    switchMap((node: any) => {
      if (!node) {
        this.logger.warn('[SensorNodesEffects] nodo non trovato nell\'entity store, SetSensors saltato');
        return [];
      }
      return [
        SensorsActions.SetSensors(
          {
            sensors:
              (node.sensors ?? []).map(sensor => (
                {
                  ...sensor,
                  loading: true,
                  icon: getSensorIcon(sensor.type),
                  cardStatus: 'basic',
                  threshold: getDefaultThreshold(sensor.type),
                  lastMeasure: null,
                  last4Measures: [],
                  last4MeasuresChartSeries: buildChartSeries(false),
                  last24hMeasures: {
                    loaded: false,
                    measures: []
                  },
                }
              ))
          }
        )
      ];
    })));



  updateSensorNode$ = createEffect(() => this.actions$.pipe(
    ofType(SensorNodesActions.PutSensorNode),
    switchMap((action) =>
      this.graphqlService.updateSensorNode
        (
          {
            input: {
              serialNumber: action.serial,
              updateSensorNodePatch: {
                set: {
                  name: action.name
                }
              }
            }
          }
        )
        .pipe(
          retry(3),
          map(res => res?.data?.updateSensorNode)
        )
    ),
    switchMap(res => [SensorNodesActions.UpdateSensorNode({ serial: res.serialNumber, name: res.name })
    ])
  )
  );

}
